import React from 'react'
import styled, { css } from 'styled-components'

import {
  IconButton,
  Flex,
  Text,
  FlexProps,
  truncateStyles,
} from '@sketch/components'

import GradientSeparator from 'components/GradientSeparator'
import { ReactComponent as Logo } from '@sketch/icons/logo-svg'
import { ReactComponent as LogoBlackIcon } from '@sketch/icons/logo-black-svg'
import NavbarFilter from './NavbarFilter'
import { NavbarLinkWrapper } from './NavbarLinkWrapper'

import { breakpoint } from '@sketch/global-styles'

const NavbarButton = styled(IconButton).attrs(({ size }) => ({
  size: size || 18,
}))`
  position: relative;
  height: 3rem;
  width: 3rem;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};

  :hover {
    color: ${({ theme }) => theme.colors.foreground.secondary.B};
  }

  /* Show the button as hovered when it's inside of a expanded dropdown */
  [aria-expanded='true'] & {
    color: ${({ theme }) => theme.colors.foreground.secondary.B};
  }

  ${props =>
    props.disabled &&
    css`
      opacity: 0.4;
    `};

  ${props => {
    if (props.active) {
      return css`
        color: ${({ theme }) => theme.colors.sketch.A};

        &:hover,
        &:focus {
          color: ${({ theme }) => theme.colors.sketch.A};
        }

        &:active {
          color: ${({ theme }) => theme.colors.sketch.A};
        }
      `
    }
  }}
`

const NavbarSection = styled(Flex)<
  FlexProps & { align?: 'start' | 'center' | 'end' }
>`
  display: flex;
  height: 100%;
  min-height: 100%;
  align-items: center;
  flex: 1 0px;
  overflow: hidden;
  justify-content: ${({ align = 'start' }) =>
    align === 'center' ? 'center' : `flex-${align}`};
`

const FixedWidthNavbarSection = styled(NavbarSection)<
  FlexProps & { align?: 'start' | 'center' | 'end' }
>`
  flex: none;
`

const NavbarLogoWrapper = styled(NavbarLinkWrapper)`
  display: flex;
  height: 3rem;
  width: 3rem;
  align-items: center;
  justify-content: center;
  padding: 0;
`

const LogoBlack = styled(LogoBlackIcon)`
  width: 20px;
  height: 18.75px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.background.primary.A};
`

const NavbarLogo = ({ variant }: { variant?: 'colored' | 'black' }) => (
  <NavbarLogoWrapper>
    {variant === 'black' ? (
      <LogoBlack />
    ) : (
      <Logo width="27px" height="25px" style={{ margin: 'auto' }} />
    )}
  </NavbarLogoWrapper>
)

export const navbarTitleCss = css`
  ${truncateStyles};

  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  min-width: 25px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`

const NavbarTitle = styled(Text).attrs({
  'data-testid': 'navbar-title',
})`
  ${navbarTitleCss};
`

const Navbar = styled.div<{
  fixed?: boolean
  isSidebarLeftOpen?: boolean
  /** There's only a single children element */
  singleContent?: boolean
}>(
  ({ theme, isSidebarLeftOpen, fixed, singleContent }) => css`
    position: relative;
    display: flex;
    /* Adding 1px to make room for border-bottom */
    height: ${theme.header.height + 1}px;
    border-bottom: 1px solid ${theme.colors.border.B};
    width: 100%;
    align-items: center;
    justify-content: ${singleContent ? 'flex-end' : 'space-between'};
    background: ${theme.colors.background.secondary.A};

    transition-timing-function: ease-in-out;
    transition-duration: 0.3s;
    transition-property: transform, opacity;

    transform: ${isSidebarLeftOpen
      ? `translateX(${theme.sidebar.leftWidth}px)`
      : undefined};

    /* For mobile, when the sidebar is opened we also need to move the banner (if there is any) */
    & + aside[role='alert'] {
      transition-timing-function: ease-in-out;
      transition-duration: 0.3s;
      transition-property: transform, opacity;

      transform: ${isSidebarLeftOpen
        ? `translateX(${theme.sidebar.leftWidth}px)`
        : undefined};
    }

    ${breakpoint('sm')`
      transform: none;
    `};

    ${fixed &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 100;
    `};
  `
)

const NavbarDivider = styled(GradientSeparator)`
  display: none;
`

export default Object.assign(Navbar, {
  Button: NavbarButton,
  Divider: NavbarDivider,
  Section: NavbarSection,
  FixedWidthSection: FixedWidthNavbarSection,
  Logo: NavbarLogo,
  Title: NavbarTitle,
  Filter: NavbarFilter,
})
